import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#restoreProductFeaturedMediaOnInitialLoad();
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * On an initial product-page visit, Shopify's gallery Liquid can place the first available
   * variant image ahead of the product's true featured image. Restore the product featured image
   * only for the first gallery render. Once the shopper selects a variant, the normal theme
   * ProductSelectEvent flow is left untouched so the selected variant can show its own media.
   */
  #restoreProductFeaturedMediaOnInitialLoad() {
    if (!window.location.pathname.includes('/products/')) return;
    if (document.documentElement.dataset.aryInitialProductGalleryHandled === 'true') return;

    document.documentElement.dataset.aryInitialProductGalleryHandled = 'true';

    const pageUrl = new URL(window.location.href);
    if (pageUrl.searchParams.has('variant') || pageUrl.searchParams.has('option_values')) return;

    const featuredImageUrl =
      document.querySelector('meta[property="og:image:secure_url"]')?.getAttribute('content') ||
      document.querySelector('meta[property="og:image"]')?.getAttribute('content');

    if (!featuredImageUrl) return;

    const getPathname = (value) => {
      if (!value) return '';

      try {
        return new URL(value, window.location.origin).pathname;
      } catch {
        return value.split('?')[0] || '';
      }
    };

    const featuredPathname = getPathname(featuredImageUrl);

    const selectFeaturedImage = () => {
      const slides = Array.from(this.querySelectorAll('slideshow-slide'));
      const featuredIndex = slides.findIndex((slide) => {
        const image = slide.querySelector('img');
        if (!image) return false;

        return getPathname(image.currentSrc || image.src) === featuredPathname;
      });

      if (featuredIndex < 0) return;

      const featuredSlide = slides[featuredIndex];
      const sticker = this.querySelector('.ary-max-product-sticker');
      const featuredMedia = featuredSlide?.querySelector('.product-media');

      if (sticker && featuredMedia && !featuredMedia.contains(sticker)) {
        featuredMedia.appendChild(sticker);
      }

      const moveFeaturedThumbnailFirst = (thumbnailStrip, selector) => {
        const thumbnails = Array.from(thumbnailStrip.querySelectorAll(selector));

        thumbnails.forEach((thumbnail, index) => {
          if (!(thumbnail instanceof HTMLElement)) return;
          thumbnail.style.order = index === featuredIndex ? '-1' : '';
        });
      };

      this.querySelectorAll('.slideshow-controls__thumbnails').forEach((thumbnailStrip) => {
        moveFeaturedThumbnailFirst(thumbnailStrip, ':scope > .slideshow-controls__thumbnail');
      });

      this.querySelectorAll('.ary-product-thumbnail-strip').forEach((thumbnailStrip) => {
        moveFeaturedThumbnailFirst(thumbnailStrip, ':scope > .ary-product-thumbnail');
      });

      if (featuredIndex > 0) {
        this.slideshow?.select(featuredIndex, undefined, { animate: false });
      }
    };

    selectFeaturedImage();
    requestAnimationFrame(selectFeaturedImage);
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   *
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
